import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Buttons, CancelButton, Drawer, Icon, TooltipButton } from '~/components';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  > *:first-child {
    flex: 1;
  }
`;

const RowAction = styled.div`
  flex: 0;
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  margin: 0 0 0.25rem 0;
`;

const SectionSubtitle = styled.p`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  margin: 0 0 0.75rem 0;
`;

const ObjectGroup = styled.div`
  margin-bottom: 1rem;
`;

const ObjectTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  margin: 0;
`;

const ObjectSubtitle = styled.span`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${colors.grey10};
  border-radius: 0.375rem;
  margin-bottom: 0.375rem;
  background: ${colors.white};
`;

const TemplateLabel = styled.div`
  flex: 1;
  font-size: 0.8125rem;
  font-weight: ${weights.medium};
`;

export default function WebhookDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const isMounted = useIsMounted();
  const { sageConnectionId } = useParams();

  const [setup, setSetup] = useState({ data: null, isReady: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getWebhookSetup();
        if (!isMounted.current) return;
        setSetup({ data, isReady: true });
      } catch ({ message }) {
        if (!isMounted.current) return;
        toast.error(message ?? 'Failed to load webhook setup.');
        setSetup({ data: null, isReady: true });
      }
    })();
  }, [api, workspace.id, sageConnectionId, isMounted, toast]);

  return (
    <Drawer isOpen title="Sage Intacct Webhook" onClose={onClose}>
      {(closeDrawer) => {
        if (!setup.isReady) return <PageLoader />;
        if (!setup.data) {
          return (
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={() => closeDrawer()}>Close</CancelButton>
              </Buttons>
            </Drawer.Actions>
          );
        }

        const { webhookUrl, sageClientId, triggers } = setup.data;

        const copy = (value, label) => {
          navigator.clipboard.writeText(value);
          toast.success(`${label} copied.`);
        };

        return (
          <>
            <Section>
              <SectionTitle>Connection Details</SectionTitle>
              <SectionSubtitle>Paste these into every trigger you create in Sage Intacct.</SectionSubtitle>
              {sageClientId && (
                <Row>
                  <ReadTextbox label="Client ID" value={sageClientId} />
                  <RowAction>
                    <TooltipButton onClick={() => copy(sageClientId, 'Client ID')} tooltip="Copy Client ID">
                      <Icon color={colors.white} icon="clipboard" />
                    </TooltipButton>
                  </RowAction>
                </Row>
              )}
              <Row>
                <ReadTextbox label="Target URL" value={webhookUrl} />
                <RowAction>
                  <TooltipButton onClick={() => copy(webhookUrl, 'URL')} tooltip="Copy URL">
                    <Icon color={colors.white} icon="clipboard" />
                  </TooltipButton>
                </RowAction>
              </Row>
            </Section>

            <Section>
              <SectionTitle>Templates</SectionTitle>
              <SectionSubtitle>One trigger per object covers its events.</SectionSubtitle>
              {triggers.map((t) => (
                <ObjectGroup key={t.object}>
                  <TemplateRow>
                    <TemplateLabel>
                      <ObjectTitle>
                        {t.name} <code>{t.object}</code>
                      </ObjectTitle>
                      <ObjectSubtitle>
                        {t.subtitle} — Events: {t.events.map((e) => `After ${e}`).join(', ')}
                      </ObjectSubtitle>
                    </TemplateLabel>
                    <TooltipButton onClick={() => copy(t.template, 'Template')} tooltip={`Copy ${t.name} template`}>
                      <Icon color={colors.white} icon="clipboard" />
                    </TooltipButton>
                  </TemplateRow>
                </ObjectGroup>
              ))}
            </Section>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={() => closeDrawer()}>Close</CancelButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
